import { Link, useParams, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const strip = { background: "linear-gradient(90deg, #e5c158 0%, #e5c158 50%, #e5c158 100%)" };
const goldBg = { background: "linear-gradient(135deg, #a87f24 0%, #e5c158 50%, #d4af37 100%)" };

const BarChart = ({ bars, caption }) => {
  const max = Math.max(...bars.map((b) => b.value));
  return (
    <div>
      <svg viewBox="0 0 640 240" className="w-full h-auto" role="img" aria-label={caption}>
        {[0.25, 0.5, 0.75, 1].map((f) => (
          <line key={f} x1="40" x2="620" y1={200 - 160 * f} y2={200 - 160 * f} stroke="#6a5c48" strokeWidth="1" strokeDasharray="3 5" />
        ))}
        <line x1="40" x2="620" y1="200" y2="200" stroke="#c9a227" strokeWidth="1.5" />
        {bars.map((b, i) => {
          const bw = Math.min(64, 540 / bars.length - 18);
          const x = 40 + (i + 0.5) * (580 / bars.length) - bw / 2;
          const h = Math.max(4, (b.value / max) * 160);
          return (
            <g key={b.label}>
              <rect x={x} y={200 - h} width={bw} height={h} fill={b.highlight ? "url(#goldBar)" : "#e5d9ba"} stroke={b.highlight ? "#a87f24" : "#d8c9a3"} strokeWidth="1" />
              <text x={x + bw / 2} y={222} textAnchor="middle" fontSize="11" fill="#cbb493" fontFamily="monospace">{b.label}</text>
              <text x={x + bw / 2} y={190 - h} textAnchor="middle" fontSize="11" fill={b.highlight ? "#e5c158" : "#cbb493"} fontFamily="monospace">{b.display}</text>
            </g>
          );
        })}
        <defs>
          <linearGradient id="goldBar" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#e5c158" />
            <stop offset="100%" stopColor="#a87f24" />
          </linearGradient>
        </defs>
      </svg>
      <p className="mt-3 text-[10px] font-mono-accent uppercase tracking-[0.25em] text-[#b09a7a] text-center">{caption}</p>
    </div>
  );
};

const CurveChart = ({ points, threshold, caption }) => {
  const max = Math.max(...points.map((p) => p.value), threshold.value);
  const px = (i) => 40 + (i / (points.length - 1)) * 580;
  const py = (v) => 200 - (v / max) * 160;
  const line = points.map((p, i) => `${i === 0 ? "M" : "L"}${px(i)},${py(p.value)}`).join(" ");
  const area = `${line} L${px(points.length - 1)},200 L40,200 Z`;
  const crossIdx = points.findIndex((p) => p.value >= threshold.value);
  return (
    <div>
      <svg viewBox="0 0 640 240" className="w-full h-auto" role="img" aria-label={caption}>
        <defs>
          <linearGradient id="goldArea" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#e5c158" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#e5c158" stopOpacity="0.05" />
          </linearGradient>
        </defs>
        {[0.25, 0.5, 0.75, 1].map((f) => (
          <line key={f} x1="40" x2="620" y1={200 - 160 * f} y2={200 - 160 * f} stroke="#6a5c48" strokeWidth="1" strokeDasharray="3 5" />
        ))}
        <line x1="40" x2="620" y1="200" y2="200" stroke="#c9a227" strokeWidth="1.5" />
        <line x1="40" x2="620" y1={py(threshold.value)} y2={py(threshold.value)} stroke="#e5c158" strokeWidth="1.5" strokeDasharray="8 6" />
        <text x="615" y={py(threshold.value) - 8} textAnchor="end" fontSize="11" fill="#e5c158" fontFamily="monospace">{threshold.label}</text>
        <path d={area} fill="url(#goldArea)" />
        <path d={line} fill="none" stroke="#c9a227" strokeWidth="2.5" />
        {crossIdx > 0 && (
          <g>
            <circle cx={px(crossIdx)} cy={py(points[crossIdx].value)} r="5" fill="#c9a227" stroke="#fffdf9" strokeWidth="2" />
            <text x={px(crossIdx)} y={py(points[crossIdx].value) - 14} textAnchor="middle" fontSize="11" fill="#f3e5ab" fontFamily="monospace">Recouped</text>
          </g>
        )}
        {points.filter((_, i) => i % Math.ceil(points.length / 6) === 0 || i === points.length - 1).map((p) => {
          const i = points.indexOf(p);
          return <text key={p.label} x={px(i)} y={222} textAnchor="middle" fontSize="11" fill="#cbb493" fontFamily="monospace">{p.label}</text>;
        })}
      </svg>
      <p className="mt-3 text-[10px] font-mono-accent uppercase tracking-[0.25em] text-[#b09a7a] text-center">{caption}</p>
    </div>
  );
};

const PRODUCTS = {
  "artist-capital-loans": {
    desc: "Artist capital loans from $25K to $1M for recording, touring, and marketing. Fixed terms, funded in a week or less, and you keep 100% of your rights.",
    gate: "Gate 01 · The Entrance",
    title: "Artist Capital Loans",
    range: "$25K – $1M",
    tagline: "Walk-out capital for recording, touring, and marketing.",
    intro:
      "A fixed-term loan sized against your streaming performance, royalty history, and credit profile. You receive the full amount up front — one predictable payment schedule, zero ownership given up.",
    breakdown: [
      ["Funding Range", "$25,000 – $1,000,000"],
      ["Structure", "Fixed-term loan with predictable monthly payments"],
      ["Speed", "Funded in a week or less after offer acceptance"],
      ["Cost", "Fixed rate negotiated around your profile — no surprises"],
      ["Your Rights", "100% retained. This is debt, not equity — masters and publishing stay yours"],
      ["Best For", "Recording budgets, tour deposits, marketing pushes, video production"],
    ],
    steps: [
      "Apply in five minutes with your streaming and royalty history",
      "We assess your catalog as recurring, bankable income",
      "Receive a fixed-term offer sized to your momentum",
      "Capital lands in your account in a week or less",
    ],
    exampleTitle: "Worked Example — $150K Recording & Tour Loan",
    exampleNote: "Illustrative only. Actual terms depend on your profile.",
    chart: {
      type: "bars",
      caption: "Capital received day one vs. cumulative repaid per quarter",
      bars: [
        { label: "Day 1", value: 150, display: "$150K", highlight: true },
        { label: "Mo 3", value: 42, display: "$42K" },
        { label: "Mo 6", value: 83, display: "$83K" },
        { label: "Mo 9", value: 125, display: "$125K" },
        { label: "Mo 12", value: 166, display: "$166K" },
      ],
    },
  },
  "label-lines-of-credit": {
    desc: "Revolving label lines of credit from $100K to $5M for signings, releases, and operations. Draw and repay on your schedule, interest only on what you use.",
    gate: "Gate 02 · The Stands",
    title: "Label Lines of Credit",
    range: "$100K – $5M",
    tagline: "Fill every seat. Revolving credit that moves at the speed of your release calendar.",
    intro:
      "A revolving line you draw on for signings, releases, and operations — and repay as revenue lands. You only pay for what you use, minimizing your cost of capital between campaigns.",
    breakdown: [
      ["Credit Limit", "$100,000 – $5,000,000 revolving"],
      ["Structure", "Draw, repay, and redraw — interest only on the outstanding balance"],
      ["Speed", "Initial facility in days; subsequent draws near-instant"],
      ["Cost", "Pay only on drawn funds — idle capital costs you nothing"],
      ["Your Rights", "100% retained across your roster's masters and publishing"],
      ["Best For", "New signings, release campaigns, payroll bridges, catalog acquisitions"],
    ],
    steps: [
      "We underwrite your label's revenue history and pipeline once",
      "Your revolving limit is established — draw whenever you need it",
      "Repay as distributor payments and royalties land",
      "Redraw for the next signing without reapplying",
    ],
    exampleTitle: "Worked Example — $500K Revolving Line In Action",
    exampleNote: "Illustrative only. Outstanding balance across a release year.",
    chart: {
      type: "bars",
      caption: "Outstanding balance by month — draw for signings, repay as revenue lands",
      bars: [
        { label: "Jan", value: 300, display: "$300K", highlight: true },
        { label: "Mar", value: 180, display: "$180K" },
        { label: "May", value: 90, display: "$90K" },
        { label: "Jul", value: 450, display: "$450K", highlight: true },
        { label: "Sep", value: 260, display: "$260K" },
        { label: "Nov", value: 50, display: "$50K" },
      ],
    },
  },
  "business-credit-cards": {
    desc: "Business credit card stacking from $10K to $250K for music businesses. Sequenced applications that stack limits without wasting inquiries.",
    gate: "Gate 03 · Midfield",
    title: "Business Credit Cards",
    range: "$10K – $250K",
    tagline: "Play the field. Stacked, curated card limits without wasting inquiries.",
    intro:
      "We hand-select from a curated network of business credit card lenders and sequence applications so your limits stack — building serious spending power while protecting your credit profile.",
    breakdown: [
      ["Combined Limits", "$10,000 – $250,000 across stacked cards"],
      ["Structure", "Multiple 0% intro-APR and premium cards sequenced strategically"],
      ["Speed", "First approvals within days of your assessment"],
      ["Cost", "0% introductory APR options; no wasted hard inquiries"],
      ["Your Rights", "Unaffected — cards are pure spending power, nothing is pledged"],
      ["Best For", "Ads, playlisting, content production, travel, day-to-day operations"],
    ],
    steps: [
      "We map your credit profile before a single inquiry is pulled",
      "Cards are sequenced across issuers so limits stack, not collide",
      "Approvals land within days — limits combine into real firepower",
      "We help you manage utilization to keep your score climbing",
    ],
    exampleTitle: "Worked Example — $120K Stacked Across Three Cards",
    exampleNote: "Illustrative only. Limits depend on your credit profile.",
    chart: {
      type: "bars",
      caption: "Individual card limits combining into $120K of total spending power",
      bars: [
        { label: "Card A", value: 45, display: "$45K" },
        { label: "Card B", value: 38, display: "$38K" },
        { label: "Card C", value: 37, display: "$37K" },
        { label: "Total", value: 120, display: "$120K", highlight: true },
      ],
    },
  },
  "catalog-advances": {
    desc: "Catalog advances from $50K to $10M against your masters and publishing. Headline-sized funding while you keep 100% ownership of your rights.",
    gate: "Gate 04 · The Main Stage",
    title: "Catalog Advances",
    range: "$50K – $10M",
    tagline: "Headline your own future. Unlock your catalog's value — keep 100% of your rights.",
    intro:
      "An advance against the future earnings of your masters and publishing. You receive a headline-sized payment now, royalties recoup the advance over time, and ownership never leaves your hands.",
    breakdown: [
      ["Advance Size", "$50,000 – $10,000,000 against catalog earnings"],
      ["Structure", "Non-recourse-style advance recouped from future royalties"],
      ["Speed", "Valuation in days; funding in a week or less after terms"],
      ["Cost", "A defined recoupment share until the advance is repaid"],
      ["Your Rights", "100% retained — you never sell your masters or publishing"],
      ["Best For", "Acquisitions, buyouts of partners, major life or tour investments"],
    ],
    steps: [
      "We value your catalog's royalty streams like the asset they are",
      "You receive a headline advance against future earnings",
      "Royalties recoup the advance automatically as they flow",
      "Once recouped, 100% of every royalty dollar is yours again",
    ],
    exampleTitle: "Worked Example — $500K Advance Recoupment Curve",
    exampleNote: "Illustrative only. Recoupment speed follows your royalty flow.",
    chart: {
      type: "curve",
      caption: "Cumulative royalties recouped vs. the $500K advance line",
      threshold: { value: 500, label: "$500K Advance" },
      points: [
        { label: "Mo 0", value: 0 },
        { label: "Mo 4", value: 130 },
        { label: "Mo 8", value: 265 },
        { label: "Mo 12", value: 390 },
        { label: "Mo 16", value: 505 },
        { label: "Mo 20", value: 640 },
        { label: "Mo 24", value: 780 },
      ],
    },
  },
};

const ProductPage = () => {
  const { slug } = useParams();
  const p = PRODUCTS[slug];
  useEffect(() => {
    if (!p) return;
    document.title = `${p.title} (${p.range}) | Emison Management`;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.content = p.desc;
    return () => {
      document.title = "Emison Management — Your Funding Partner";
    };
  }, [p]);
  if (!p) return <Navigate to="/" replace />;

  return (
    <main data-testid={`product-page-${slug}`} className="pt-20 bg-white">
      {/* HERO */}
      <section className="relative overflow-hidden py-20 lg:py-28 px-6 lg:px-12">
        <span
          aria-hidden="true"
          className="pointer-events-none select-none absolute top-8 left-1/2 -translate-x-1/2 font-serif italic whitespace-nowrap text-[18vw] leading-none text-transparent"
          style={{ WebkitTextStroke: "1px rgba(201,162,39,0.14)" }}
        >
          {p.title.split(" ").slice(-1)}
        </span>
        <div className="relative max-w-5xl mx-auto">
          <Reveal>
            <Link
              to="/"
              data-testid="product-back-link"
              className="inline-flex items-center gap-2 text-[11px] font-mono-accent uppercase tracking-[0.25em] text-champagne hover:text-gold transition-colors mb-10"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back to the stadium
            </Link>
            <div className="flex items-center gap-4 mb-8">
              <span
                className="w-11 h-11 rotate-45 flex items-center justify-center shadow-[0_8px_20px_rgba(201,162,39,0.4)]"
                style={goldBg}
              >
                <span className="-rotate-45 font-serif italic text-white text-base">E</span>
              </span>
              <p className="text-[10px] font-mono-accent uppercase tracking-[0.4em] text-gold">
                {p.gate}
              </p>
              <span className="h-px w-16 sm:w-24 bg-gradient-to-r from-[#c9a227]/60 to-transparent" />
            </div>
            <h1 className="font-serif font-semibold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#4D4232] leading-[1.08]">
              {p.title.split(" ").slice(0, -1).join(" ")}{" "}
              <span className="italic gold-text">{p.title.split(" ").slice(-1)}</span>
            </h1>
            <span className="inline-block mt-8 font-serif font-semibold text-2xl gold-text">{p.range}</span>
            <p className="mt-6 text-base sm:text-lg text-[#5c4a34] leading-relaxed max-w-2xl">{p.tagline}</p>
          </Reveal>
        </div>
      </section>

      {/* INTRO + STEPS */}
      <section className="px-6 lg:px-12 pb-20">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10">
          <Reveal>
            <div className="relative bg-[#4D4232] border border-[#6a5c48] p-8 lg:p-10 shadow-[0_20px_60px_rgba(45,26,11,0.15)] h-full">
              <span className="absolute bottom-0 left-0 right-0 h-[3px]" style={strip} />
              <h2 className="font-serif text-2xl text-[#f3e5c8] mb-5">The breakdown</h2>
              <p className="text-sm sm:text-base text-[#d8cdb8] leading-relaxed">{p.intro}</p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="relative bg-[#4D4232] border border-[#6a5c48] p-8 lg:p-10 shadow-[0_20px_60px_rgba(45,26,11,0.15)] h-full">
              <span className="absolute bottom-0 left-0 right-0 h-[3px]" style={strip} />
              <h2 className="font-serif text-2xl text-[#f3e5c8] mb-5">How it works</h2>
              <ul className="space-y-4">
                {p.steps.map((s, i) => (
                  <li key={s} className="flex items-start gap-4">
                    <span
                      className="w-6 h-6 rotate-45 flex items-center justify-center shrink-0 mt-0.5"
                      style={goldBg}
                    >
                      <span className="-rotate-45 font-mono-accent text-[9px] text-white">{i + 1}</span>
                    </span>
                    <span className="text-sm text-[#d8cdb8] leading-relaxed">{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FULL BREAKDOWN TABLE */}
      <section className="px-6 lg:px-12 pb-20">
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <div className="relative bg-[#4D4232] border border-[#6a5c48] shadow-[0_20px_60px_rgba(45,26,11,0.15)]">
              <span className="absolute bottom-0 left-0 right-0 h-[3px]" style={strip} />
              <div className="p-8 lg:p-10">
                <h2 className="font-serif text-2xl text-[#f3e5c8] mb-2">Full terms at a glance</h2>
                <div className="divide-y divide-[#6a5c48]">
                  {p.breakdown.map(([k, v]) => (
                    <div key={k} className="grid grid-cols-1 sm:grid-cols-3 gap-1 sm:gap-6 py-4">
                      <span className="text-[10px] font-mono-accent uppercase tracking-[0.25em] text-gold pt-1">{k}</span>
                      <span className="sm:col-span-2 text-sm text-[#d8cdb8] leading-relaxed">{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* EXAMPLE CHART */}
      <section className="px-6 lg:px-12 pb-24">
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <div className="relative bg-[#4D4232] border border-[#6a5c48] p-8 lg:p-12 shadow-[0_20px_60px_rgba(45,26,11,0.15)]">
              <span className="absolute bottom-0 left-0 right-0 h-[3px]" style={strip} />
              <div className="flex flex-wrap items-baseline justify-between gap-3 mb-8">
                <h2 className="font-serif text-2xl text-[#f3e5c8]">{p.exampleTitle}</h2>
                <span className="text-[10px] font-mono-accent uppercase tracking-[0.25em] text-[#b09a7a]">{p.exampleNote}</span>
              </div>
              {p.chart.type === "bars" ? (
                <BarChart bars={p.chart.bars} caption={p.chart.caption} />
              ) : (
                <CurveChart points={p.chart.points} threshold={p.chart.threshold} caption={p.chart.caption} />
              )}
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section
        className="relative overflow-hidden px-6 lg:px-12 py-24 lg:py-28 text-center border-t border-[#4D4232]"
        style={{ background: "linear-gradient(155deg, #a87f24 0%, #e5c158 38%, #d4af37 62%, #8c6d3f 100%)" }}
      >
        <span className="absolute top-6 left-6 w-3 h-3 rotate-45 bg-[#4D4232] opacity-80" />
        <span className="absolute top-6 right-6 w-3 h-3 rotate-45 bg-[#4D4232] opacity-80" />
        <span className="absolute bottom-6 left-6 w-3 h-3 rotate-45 bg-[#4D4232] opacity-80" />
        <span className="absolute bottom-6 right-6 w-3 h-3 rotate-45 bg-[#4D4232] opacity-80" />
        <Reveal>
          <h2 className="font-serif font-semibold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#4D4232] leading-[1.1]">
            Ready to <span className="italic text-[#3d3427]">unlock the vault?</span>
          </h2>
          <p className="mt-6 text-sm text-[#4a3c2c] max-w-md mx-auto leading-relaxed">
            Five minutes. Zero obligation. A real answer within 24 hours.
          </p>
          <Link
            to="/apply"
            data-testid="product-cta-apply"
            className="mt-10 inline-flex items-center gap-3 bg-[#4D4232] text-[#e5c158] text-[11px] font-semibold font-mono-accent uppercase tracking-[0.25em] px-10 py-5 shadow-[0_15px_40px_rgba(45,26,11,0.4)] hover:bg-[#3d3427] hover:-translate-y-0.5 transition-all"
          >
            Begin Application <ArrowRight className="w-4 h-4" />
          </Link>
        </Reveal>
      </section>
    </main>
  );
};

export default ProductPage;
